import * as React from "react";
import { Table } from "semantic-ui-react";
import { values } from "lodash";

import { ContextAwareProgressBarQuestionnaireSection } from "@teamprevent/common/questionnaires/components/ContextAwareProgressBarQuestionnaireSection";
import { CustomQuestionConfig, getQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { MultiButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { YesNoWithFollowUpQuestion } from "@teamprevent/common/questionnaires/components/questions/YesNoWithFollowUpQuestion";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { NumberQuestion } from "@teamprevent/common/questionnaires/components/questions/NumberQuestion";
import { DropdownOption } from "@teamprevent/common/questionnaires/model";
import { YesOrNo } from "@teamprevent/common/questionnaires/components/questions/RadioButtonQuestion";
import { useQuestionnaireContainerForSection } from "@teamprevent/common/questionnaires";

import * as questionIds from "../consultationQuestions/questionIds";
import { sections } from "../consultation";

const section1Questions = [
    questionIds.HasYourDoctorEverSaidThatYouHaveAHeartCondition,
    questionIds.DoYouFeelPainInYourChestWhenYouDoPhysicalActivities,
    questionIds.InThePastMonthHaveYouHadChestPainWhenYouWereNotDoingPhysicalActivity,
    questionIds.DoYouLoseYourBalanceBecauseOfDizzinessOrDoYouEverLoseConsciousness,
    questionIds.DoYouHaveABoneOrJointProblemThatCouldBeMadeWorseByAChangeInPhysicalActivity,
    questionIds.IsYourDoctorCurrentlyPrescribingDrugsForBloodPressureOrAHeartCondition,
    questionIds.DoYouKnowOfAnyOtherReasonsWhyYouShouldNotDoPhysicalActivity,
    questionIds.DoYouSufferFromDiabetes,
    questionIds.DoYouSufferFromStomachOrIntestinalDisorders,
];
const chesterStepOption: DropdownOption = { value: "ChesterStep", text: "Chester Step" };
const chesterWalkOption: DropdownOption = { value: "ChesterWalk", text: "Chester Walk" };
const typeOfTestOptions = [chesterStepOption, chesterWalkOption];

const stepHeightOptions: DropdownOption[] = ["15cm", "20cm", "25cm", "30cm"].map(text => ({ value: text, text }));

interface HealthSurveillanceConsultationChesterStepSectionProps {
    questionConfigs: CustomQuestionConfig[];
}

export const HealthSurveillanceConsultationChesterStepSection: React.FC<HealthSurveillanceConsultationChesterStepSectionProps>
    = (props: HealthSurveillanceConsultationChesterStepSectionProps) => {
        const { readonly, section: { questions } } = useQuestionnaireContainerForSection(sections.ChesterStep.id);
        const questionsArray = values(questions || []);

        const unableToCarryOutMedicalAnswer = getAnswerToQuestionWithId(questionsArray, questionIds.ChesterStepUnableToCarryOutMedical);

        const section1AllAnswered = section1Questions.every(q => getAnswerToQuestionWithId(questionsArray, q) !== undefined);
        const section1AnyYes = section1Questions.some(q => getAnswerToQuestionWithId(questionsArray, q) === MultiButtonQuestion.YesOption);

        const typeOfTest = getAnswerToQuestionWithId(questionsArray, questionIds.ChesterStepTypeOfTest);

        return (
            <ContextAwareProgressBarQuestionnaireSection
                {...props}
                {...sections.ChesterStep}
            >
                {YesNoWithFollowUpQuestion({
                    readonly,
                    questionConfigs: [
                        getQuestionConfig(props.questionConfigs, questionIds.ChesterStepUnableToCarryOutMedical),
                        getQuestionConfig(props.questionConfigs, questionIds.ChesterStepUnableToCarryOutMedicalFurtherDetails)
                    ],
                    questions,
                    mandatory: true,
                    followUpMandatory: true
                })}
                {(!unableToCarryOutMedicalAnswer || unableToCarryOutMedicalAnswer === YesOrNo.No)
                    && Section1Questions({ readonly, questionConfigs: props.questionConfigs })}
                {section1AllAnswered && section1AnyYes && (<TextQuestion readonly={readonly} mandatory multiline
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.IfYesToAnyAerobicFitnessAssessmentShouldBePostponed)} />)}
                {section1AllAnswered && !section1AnyYes && (<DropdownQuestion readonly={readonly} mandatory
                    options={typeOfTestOptions}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ChesterStepTypeOfTest)} />)}
                {section1AllAnswered && !section1AnyYes && typeOfTest === chesterStepOption.value && (
                    Section2Questions({
                        readonly,
                        questionConfigs: props.questionConfigs
                    }))}
                {section1AllAnswered && !section1AnyYes && typeOfTest === chesterWalkOption.value && (<TextQuestion
                    readonly={readonly} mandatory multiline
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ChesterStepNotes)} />)}
            </ContextAwareProgressBarQuestionnaireSection>
        );
    };

interface SectionQuestionsProps {
    readonly: boolean;
    questionConfigs: CustomQuestionConfig[];
}

const Section1Questions: React.FC<SectionQuestionsProps> = (props) => (
    <>
        <MultiButtonQuestion
            mandatory
            readonly={props.readonly}
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HasYourDoctorEverSaidThatYouHaveAHeartCondition)} />
        <MultiButtonQuestion
            mandatory
            readonly={props.readonly}
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DoYouFeelPainInYourChestWhenYouDoPhysicalActivities)} />
        <MultiButtonQuestion
            mandatory
            readonly={props.readonly}
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.InThePastMonthHaveYouHadChestPainWhenYouWereNotDoingPhysicalActivity)} />
        <MultiButtonQuestion
            mandatory
            readonly={props.readonly}
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DoYouLoseYourBalanceBecauseOfDizzinessOrDoYouEverLoseConsciousness)} />
        <MultiButtonQuestion
            mandatory
            readonly={props.readonly}
            questionConfig={getQuestionConfig(
                props.questionConfigs,
                questionIds.DoYouHaveABoneOrJointProblemThatCouldBeMadeWorseByAChangeInPhysicalActivity)} />
        <MultiButtonQuestion
            mandatory
            readonly={props.readonly}
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.IsYourDoctorCurrentlyPrescribingDrugsForBloodPressureOrAHeartCondition)} />
        <MultiButtonQuestion
            mandatory
            readonly={props.readonly}
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DoYouKnowOfAnyOtherReasonsWhyYouShouldNotDoPhysicalActivity)} />
        <MultiButtonQuestion
            mandatory
            readonly={props.readonly}
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DoYouSufferFromDiabetes)} />
        <MultiButtonQuestion
            mandatory
            readonly={props.readonly}
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DoYouSufferFromStomachOrIntestinalDisorders)} />
    </>);

const Section2Questions: React.FC<SectionQuestionsProps> = (props) => (
    <>
        <MultiButtonQuestion
            readonly={props.readonly}
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.IsTheRoomQuietWellVentilatedAndBetween18To20DegreesCelsius)} />
        <MultiButtonQuestion
            readonly={props.readonly}
            questionConfig={getQuestionConfig(
                props.questionConfigs,
                questionIds.IsThereEasyAccessToATelephoneOrHotlineForEmergencyMedicalServicesWithAGeographicDescriptionOfTheSiteAccessAndLocation)} />
        <MultiButtonQuestion
            readonly={props.readonly}
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HaveYouCheckedTheAutomatedExternalDefibrillatorIsChargedAndAvailable)} />
        <MultiButtonQuestion
            readonly={props.readonly}
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HaveYouHadTrainingInTheUseOfAnExternalAutomatedDefibrillator)} />
        <MultiButtonQuestion
            readonly={props.readonly}
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HaveYouCheckedTheStepIsSecure)} />
        <MultiButtonQuestion
            readonly={props.readonly}
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DoesTheHeartRateMonitorWork)} />
        <MultiButtonQuestion
            readonly={props.readonly}
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.DoesTheMetronomeWork)} />
        <MultiButtonQuestion
            readonly={props.readonly}
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HaveYouHadBLSTraining)} />
        <MultiButtonQuestion
            readonly={props.readonly}
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HaveYouCheckedThereAreNoContraIndicationsIdentified)} />
        <MultiButtonQuestion
            readonly={props.readonly}
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HaveTheySuitableWearingLooseFittingComfortableClothing)} />
        <MultiButtonQuestion
            readonly={props.readonly}
            questionConfig={getQuestionConfig(
                props.questionConfigs,
                questionIds.HaveYouCheckedThatTheSubjectHasNotEatenSmokedDrunkTeaOrCoffeeForAtLeastTwoHoursBeforeTheTest)} />
        <MultiButtonQuestion
            readonly={props.readonly}
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HaveYouBriefedThemOnTheTest)} />
        <MultiButtonQuestion
            readonly={props.readonly}
            questionConfig={getQuestionConfig(
                props.questionConfigs,
                questionIds.HaveYouCheckedThatTheSubjectIsNotSufferingFromAnyMuscularInjuryConditionWhichWillAffectHisHerAbility)} />
        <MultiButtonQuestion
            readonly={props.readonly}
            questionConfig={getQuestionConfig(
                props.questionConfigs,
                questionIds.HaveYouCheckedTheSubjectIsNotRecoveringFromAnAcuteIllnessOrHasAColdOrIsTakingBetaBlockerDrugsWhichWillDepressHeartRateScores)} />
        <DropdownQuestion
            popupContent={stepHeightPopupContent}
            readonly={props.readonly}
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.StepHeight)}
            options={stepHeightOptions} />
        <Table>
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell />
                    <Table.HeaderCell content="Pulse (bpm)" />
                    <Table.HeaderCell content="RPE" />
                </Table.Row>
            </Table.Header>
            <Table.Body>
                <Table.Row>
                    <Table.Cell content="At rest/0 minutes*" />
                    <Table.Cell>
                        <NumberQuestion mandatory readonly={props.readonly} min={1} max={300} noLabel hideDivider
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PulseBpm0Minutes)} />
                    </Table.Cell>
                    <Table.Cell />
                </Table.Row>
                <Table.Row>
                    <Table.Cell content="2 minutes*" />
                    <Table.Cell>
                        <NumberQuestion mandatory readonly={props.readonly} min={1} max={300} noLabel hideDivider
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PulseBpm2Minutes)} />
                    </Table.Cell>
                    <Table.Cell>
                        <NumberQuestion mandatory readonly={props.readonly} min={1} max={30} noLabel hideDivider
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RPE2Minutes)} />
                    </Table.Cell>
                </Table.Row>
                <Table.Row>
                    <Table.Cell content="4 minutes*" />
                    <Table.Cell>
                        <NumberQuestion mandatory readonly={props.readonly} min={1} max={300} noLabel hideDivider
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PulseBpm4Minutes)} />
                    </Table.Cell>
                    <Table.Cell>
                        <NumberQuestion mandatory readonly={props.readonly} min={1} max={30} noLabel hideDivider
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RPE4Minutes)} />
                    </Table.Cell>
                </Table.Row>
                <Table.Row>
                    <Table.Cell content="6 minutes*" />
                    <Table.Cell>
                        <NumberQuestion mandatory readonly={props.readonly} min={1} max={300} noLabel hideDivider
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PulseBpm6Minutes)} />
                    </Table.Cell>
                    <Table.Cell>
                        <NumberQuestion mandatory readonly={props.readonly} min={1} max={30} noLabel hideDivider
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RPE6Minutes)} />
                    </Table.Cell>
                </Table.Row>
                <Table.Row>
                    <Table.Cell content="8 minutes" />
                    <Table.Cell>
                        <NumberQuestion readonly={props.readonly} min={1} max={300} noLabel hideDivider
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PulseBpm8Minutes)} />
                    </Table.Cell>
                    <Table.Cell>
                        <NumberQuestion readonly={props.readonly} min={1} max={30} noLabel hideDivider
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RPE8Minutes)} />
                    </Table.Cell>
                </Table.Row>
                <Table.Row>
                    <Table.Cell content="10 minutes" />
                    <Table.Cell>
                        <NumberQuestion readonly={props.readonly} min={1} max={300} noLabel hideDivider
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PulseBpm10Minutes)} />
                    </Table.Cell>
                    <Table.Cell>
                        <NumberQuestion readonly={props.readonly} min={1} max={30} noLabel hideDivider
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RPE10Minutes)} />
                    </Table.Cell>
                </Table.Row>
            </Table.Body>
        </Table>
        <TextQuestion readonly={props.readonly} multiline
            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ChesterStepAdditionalNotes)} />
    </>);

const stepHeightPopupContent = (
    <>
        Chester Step Height
        <ul>
            <li>30cm - Under 40 years of age who take regular physical exercise and are used to moderately vigorous exertion.</li>
            <li>25cm - Over 40 years of age who take regular physical exercise and are used to moderately vigorous exertion.</li>
            <li>20cm - Under 40 years of age who take little or no regular physical exercise and for those under-40s who are moderately overweight.</li>
            <li>15cm - Over 40 years of age who take little or no physical exercise and for those over-40s who are moderately overweight.</li>
        </ul>
        NB. For testing the over-60s, the lower step height of 15cm or 20cm is generally best.`
    </>);
